import clsx from 'clsx';
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import config from '~/config';
import { useBodyScrollLock } from '~/hooks/useBodyScrollLock';
import './style.css';
import '~/components/navigation/Menu/close-button.css';

export const modalClassNames = clsx(
	'modal m-0',
	'w-[90dvw] h-[90dvh] sm:w-96 sm:min-h-[700px]',
	'h-auto z-10 rounded-layout',
	'p-7',
	'drop-shadow-md',
	'overflow-auto md:overflow-visible',
);

const Modal: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dialogRef = useRef<HTMLDialogElement>(null);

	const open = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		dialogRef.current?.showModal();
		setIsOpen(true);
	};

	const close = (event: MouseEvent<HTMLButtonElement | HTMLDialogElement>) => {
		event.preventDefault();
		dialogRef.current?.close();
		setIsOpen(false);
	};

	useBodyScrollLock({ lock: isOpen });

	return (
		<>
			<button
				type="button"
				className="receipt-modal-open-button w-[20px] h-[20px] rounded-full font-button text-[10px] flex items-center justify-center focus:outline-none focus:ring-4"
				onClick={open}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="6.308"
					height="11.593"
					viewBox="0 0 6.308 11.593"
				>
					<g>
						<path
							d="M1 3.154a2.153 2.153 0 1 1 3.448 1.725 3.233 3.233 0 0 0-1.29 2.589v.24"
							fill="none"
							stroke="currentColor"
							strokeLinecap={config.areElementsRounded ? 'round' : 'butt'}
							strokeMiterlimit="10"
							strokeWidth="2"
						/>
						<path
							d="M3.154 11.593a1.163 1.163 0 1 1 1.163-1.163 1.163 1.163 0 0 1-1.163 1.163"
							fill="currentColor"
						/>
					</g>
				</svg>
			</button>
			<dialog
				onClick={close}
				ref={dialogRef}
				className={clsx(
					'modal overflow-auto rounded-layout drop-shadow-md',
					'max-w-full w-[400px] h-[min-content] max-h-[90dvh]',
					'relative flex-col space-y-4 p-6 md:p-10',
					isOpen ? 'flex' : '',
				)}
			>
				<button
					type="button"
					title="Close help menu"
					className="close-button w-touch-target h-touch-target flex items-center justify-center absolute right-0 top-0"
					onClick={close}
				>
					<span className="sr-only">Navigation menu close button</span>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 11.414 11.414"
						className="w-[16px] h-[16px]"
					>
						<path
							fill="none"
							stroke="currentColor"
							strokeWidth="2px"
							d="M.707.707l10 10M10.707.707l-10 10"
						/>
					</svg>
				</button>
				{children}
			</dialog>
		</>
	);
};

export default Modal;
